// v4.9 — Espace commerçant : ma boutique, mes catalogues, mes produits, partage réseaux sociaux.
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Plus, Trash2, Eye, ChevronRight, Package, Share2, Store } from "lucide-react";
import { toast } from "sonner";
import { ProductEditor } from "@/components/pro/ProductEditor";
import { ShareSheet } from "@/components/pro/ShareSheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const IMG = "https://static.prod-images.emergentagent.com/jobs/ecd25164-99ae-4ee1-9055-a64268d19618/images";
const COVER_CHOICES = [
  { key: "general", label: "Général", url: `${IMG}/0f06faeb1e812c737865a8c6a1e879cd26079f4718258e1d0cdb0e078e071125.jpeg` },
  { key: "promos", label: "Promos", url: `${IMG}/83f24b58ebd249e582daaf366d2abdf3d0be6974f2b1543234f7e25c583838f0.jpeg` },
  { key: "rentree", label: "Rentrée", url: `${IMG}/521fcfbb2c26557fb006f7c473358904ddac754284b4f946d3a8246fcedc4457.jpeg` },
  { key: "mode", label: "Mode", url: `${IMG}/695668b4f10370b25eebe98a47eaf982cbb0018a5019c2c711e82e0432e753f0.jpeg` },
  { key: "tech", label: "Tech", url: `${IMG}/da1157a1b09eeadfb89ce9702cda1d48922a14c0a119affd9e5e9f2b99ab48e5.jpeg` },
  { key: "librairie", label: "Librairie", url: `${IMG}/1b7d21ab468ad515bf71bea03601d92e5962d9cd13ac34025645513053cee82e.jpeg` },
];
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` } });
const abs = (u) => (u && !u.startsWith("http") ? `${BACKEND_URL}${u}` : u);

export default function MerchantCatalogsPage() {
  const navigate = useNavigate();
  const [shop, setShop] = useState(undefined);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", cover: COVER_CHOICES[0].url });
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [productsFor, setProductsFor] = useState(null);
  const [sharing, setSharing] = useState(null);

  const load = useCallback(() => {
    axios.get(`${BACKEND_URL}/api/v3/shop/mine`, auth()).then((r) => setShop(r.data?.shop || null)).catch(() => setShop(null));
    axios.get(`${BACKEND_URL}/api/v3/catalogs/mine`, auth()).then((r) => setMine(r.data?.catalogs || [])).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Donne un titre à ton catalogue"); return; }
    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/v3/catalogs`, { title: form.title, description: form.description, coverImage: form.cover }, auth());
      toast.success("Catalogue publié ✅ Ajoute maintenant tes produits");
      setForm({ title: "", description: "", cover: COVER_CHOICES[0].url });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Connecte-toi d'abord" : "Erreur — réessaie");
    } finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce catalogue et tous ses produits ? Cette action est définitive.")) return;
    await axios.delete(`${BACKEND_URL}/api/v3/catalogs/${id}`, auth()).catch(() => {});
    toast.success("Catalogue retiré");
    load();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0d] text-white pb-16" data-testid="merchant-catalogs-page">
      <div className="sticky top-0 z-10 bg-[#0a0a0d]/95 backdrop-blur border-b border-white/8">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/10 text-white/80" data-testid="mc-back-btn"><ArrowLeft size={19} /></button>
          <div className="flex-1">
            <div className="text-[15px] font-bold">🏪 Mes catalogues</div>
            <div className="text-[10.5px] text-white/45">Ta boutique, tes catalogues, tes produits — visibles sur le Kiosque AYA</div>
          </div>
          {shop && (
            <button onClick={() => setShowForm((v) => !v)} data-testid="mc-new-btn" className="rounded-xl bg-amber-400 text-black font-bold text-[12px] px-3.5 py-2 flex items-center gap-1"><Plus size={14} /> Nouveau</button>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-5">
        {/* ── Ma boutique (résolue automatiquement : mini-site / profil pro) ── */}
        {shop === undefined ? (
          <div className="h-20 rounded-2xl bg-white/[0.05] animate-pulse" />
        ) : shop === null ? (
          <div className="rounded-2xl border border-amber-400/25 bg-white/[0.04] p-4 text-center space-y-2" data-testid="mc-no-shop">
            <div className="text-2xl">🏪</div>
            <div className="text-[13px] font-bold">Configure d'abord ta boutique</div>
            <div className="text-[11.5px] text-white/50">Tes catalogues sont publiés au nom de ta boutique. Crée ta fiche et tes produits dans « Ma boutique ».</div>
            <button onClick={() => navigate("/mon-compte?tab=pro")} data-testid="mc-goto-boutique" className="rounded-xl bg-amber-400 text-black font-bold text-[12px] px-4 py-2 mt-1">Gérer ma boutique</button>
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-3.5 py-3" data-testid="mc-shop-banner">
            <div className="w-10 h-10 rounded-xl bg-amber-400/20 flex items-center justify-center shrink-0"><Store size={18} className="text-amber-300" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-extrabold truncate">{shop.name}</div>
              <div className="text-[10.5px] text-white/50 truncate capitalize">{shop.category}{shop.location ? ` · ${shop.location}` : ""}{shop.whatsapp ? ` · ${shop.whatsapp}` : ""}</div>
            </div>
          </div>
        )}

        {/* ── Création de catalogue ── */}
        {shop && showForm && (
          <div className="rounded-2xl border border-amber-400/25 bg-white/[0.04] p-4 space-y-3" data-testid="mc-form">
            <div className="text-[12px] font-extrabold text-amber-300">Nouveau catalogue — {shop.name}</div>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre du catalogue (ex : Promo Tabaski)" data-testid="mc-title-input"
              className="w-full bg-[#141416] border border-white/12 rounded-xl px-3 py-2.5 text-[13px] placeholder:text-white/30" maxLength={60} />
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Courte description (optionnel)" data-testid="mc-desc-input"
              className="w-full bg-[#141416] border border-white/12 rounded-xl px-3 py-2.5 text-[13px] placeholder:text-white/30" maxLength={160} />
            <div>
              <div className="text-[11px] font-bold text-white/60 mb-1.5">Couverture</div>
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {COVER_CHOICES.map((c) => (
                  <button key={c.key} onClick={() => setForm({ ...form, cover: c.url })} data-testid={`mc-cover-${c.key}`}
                    className={`shrink-0 w-14 rounded-lg overflow-hidden border-2 ${form.cover === c.url ? "border-amber-400" : "border-transparent"}`}>
                    <img src={c.url} alt={c.label} className="aspect-[3/4] object-cover" />
                    <div className="text-[8px] py-0.5 bg-black/60">{c.label}</div>
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2.5">
                <label className="flex-1 rounded-xl border border-white/15 text-center text-[11.5px] font-semibold py-2 cursor-pointer hover:border-white/35" data-testid="mc-upload-label">
                  📷 Ma photo
                  <input type="file" accept="image/*" className="hidden" data-testid="mc-upload-input" onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 3_000_000) { toast.error("Image trop lourde (max 3 Mo)"); return; }
                    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
                    try {
                      const r = await axios.post(`${BACKEND_URL}/api/v3/catalogs/upload-cover`, { image_base64: b64 }, auth());
                      setForm((fm) => ({ ...fm, cover: r.data.url }));
                      toast.success("Photo ajoutée ✅");
                    } catch { toast.error("Échec de l'envoi"); }
                  }} />
                </label>
                <button
                  disabled={aiBusy}
                  data-testid="mc-ai-btn"
                  onClick={async () => {
                    const desc = window.prompt("Décris la couverture souhaitée (ex : fournitures scolaires colorées sur fond crème)");
                    if (!desc || !desc.trim()) return;
                    setAiBusy(true);
                    toast.info("✨ Génération de la couverture en cours (~20 s)…");
                    try {
                      const r = await axios.post(`${BACKEND_URL}/api/v3/catalogs/generate-cover`, { prompt: desc }, { ...auth(), timeout: 95000 });
                      setForm((fm) => ({ ...fm, cover: r.data.url }));
                      toast.success("Couverture générée par IA ✅");
                    } catch { toast.error("Génération impossible — réessaie"); }
                    finally { setAiBusy(false); }
                  }}
                  className="flex-1 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-black font-bold text-[11.5px] py-2 disabled:opacity-50"
                >
                  {aiBusy ? "Génération…" : "✨ Générer par IA"}
                </button>
              </div>
              {form.cover && !COVER_CHOICES.some((c) => c.url === form.cover) && (
                <img src={abs(form.cover)} alt="Aperçu" data-testid="mc-cover-preview" className="mt-2.5 w-24 aspect-[3/4] object-cover rounded-lg border-2 border-amber-400" />
              )}
            </div>
            <button onClick={create} disabled={creating} data-testid="mc-publish-btn" className="w-full rounded-xl bg-amber-400 text-black font-bold text-[13px] py-2.5 disabled:opacity-50">
              {creating ? "Publication…" : "Publier sur le Kiosque"}
            </button>
          </div>
        )}

        {/* ── Mes catalogues ── */}
        {shop && mine.length === 0 && !showForm ? (
          <div className="text-center py-10 space-y-2">
            <div className="text-3xl">🏪</div>
            <div className="text-[14px] font-bold">Aucun catalogue publié</div>
            <div className="text-[12px] text-white/50">Crée ton premier catalogue, puis ajoute tes produits : tes clients les découvriront dans le Kiosque AYA.</div>
          </div>
        ) : (
          <div className="space-y-2.5">
            {mine.map((c) => (
              <div key={c.catalogId} className="rounded-2xl bg-white/[0.04] border border-white/10 px-3 py-2.5 space-y-2" data-testid={`mc-item-${c.catalogId}`}>
                <div className="flex items-center gap-3">
                  <img src={abs(c.coverImage)} alt="" className="w-10 h-[52px] rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-bold truncate">{c.title}</div>
                    <div className="text-[10.5px] text-white/45 truncate">{c.merchantName} · {c.productCount} produit{c.productCount > 1 ? "s" : ""} · publié</div>
                  </div>
                  <button onClick={() => navigate(`/kiosque/catalogue/${c.catalogId}`)} className="p-2 rounded-full hover:bg-white/10 text-white/70" data-testid={`mc-view-${c.catalogId}`}><Eye size={16} /></button>
                  <button onClick={() => remove(c.catalogId)} className="p-2 rounded-full hover:bg-white/10 text-red-400/80" data-testid={`mc-delete-${c.catalogId}`}><Trash2 size={16} /></button>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setProductsFor(c)} data-testid={`mc-products-${c.catalogId}`}
                    className="flex-1 rounded-xl bg-amber-400/15 border border-amber-400/30 text-amber-200 font-bold text-[11.5px] py-2 flex items-center justify-center gap-1.5">
                    <Package size={13} /> Gérer les produits
                  </button>
                  <button onClick={() => setSharing(c)} data-testid={`mc-share-${c.catalogId}`}
                    className="flex-1 rounded-xl border border-white/15 text-white/75 font-bold text-[11.5px] py-2 flex items-center justify-center gap-1.5 hover:border-white/35">
                    <Share2 size={13} /> Partager
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <button onClick={() => navigate("/kiosque/catalogues")} className="w-full rounded-2xl border border-white/12 bg-white/[0.04] hover:border-white/30 px-4 py-3.5 text-left flex items-center justify-between transition-colors" data-testid="mc-see-public">
          <span className="text-[12.5px] font-semibold text-white/70">Voir la page publique des catalogues</span>
          <ChevronRight size={16} className="text-white/40" />
        </button>
      </div>

      {productsFor && <ProductEditor catalog={productsFor} onClose={() => setProductsFor(null)} onChanged={load} />}
      {sharing && <ShareSheet catalog={sharing} onClose={() => setSharing(null)} />}
    </div>
  );
}
